import React from "react";
import { FileText } from "lucide-react";
import { Callout, EmptyState } from "../../../ui";
import Shimmer from "../../../Common/Shimmer";
import DocumentsPanel from "../../profile/DocumentsPanel";
import useProfileOptions from "../../profile/useProfileOptions";
import { documentCounts, experienceMonths } from "../../profile/studentProfileRules";

/**
 * DocumentsTab — the student's papers on the admin record, filed by section,
 * with upload and remove for an editor and verify / reject for a reviewer
 * (contract students). Both rights are decided by the page.
 *
 * Props:
 *   student    — the record (experience and work history decide which sections apply)
 *   docs       — useStudentDocuments(staffDocumentsApi(id))
 *   canEdit    — may upload and remove
 *   canVerify  — may verify and reject
 */
const DocumentsTab = ({ student, docs, canEdit, canVerify }) => {
  const { options } = useProfileOptions();
  const counts = documentCounts(docs.documents);

  if (docs.loading && !docs.profile) return <Shimmer height={120} count={3} />;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {docs.documents.length > 0 && (
        <p className="tw-m-0 tw-text-sm tw-text-content-muted">
          {counts.total} document{counts.total === 1 ? "" : "s"} · {counts.verified} verified · {counts.pending} awaiting check
          {counts.rejected ? ` · ${counts.rejected} rejected` : ""}
        </p>
      )}
      {!canEdit && docs.documents.length === 0 ? (
        <EmptyState icon={FileText} title="No documents yet" description="Nothing has been uploaded for this student." />
      ) : (
        <DocumentsPanel
          sections={options.documentSections}
          documents={docs.documents}
          experienceMonths={experienceMonths(student.experience)}
          workHistory={student.workHistory || []}
          accept={options.documentAccept}
          imageAccept={options.imageAccept}
          maxMb={options.documentMaxMb}
          readOnly={!canEdit}
          busy={docs.busy}
          onUpload={canEdit ? docs.upload : undefined}
          onRemove={canEdit ? docs.remove : undefined}
          onVerify={canVerify ? docs.verify : undefined}
        />
      )}
      {docs.error && <Callout tone="danger" role="alert">{docs.error}</Callout>}
      {canEdit && docs.documents.length === 0 && !docs.error && (
        <Callout tone="info">Upload each document against its name above. A student can also upload their own from My Profile.</Callout>
      )}
    </div>
  );
};

export default DocumentsTab;
